import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { attemptGuard } from './core/guards/attempt.guard';
import { guestGuard } from './core/guards/guest.guard';
import { signGuard } from './core/guards/sign.guard';

/**
 * Top-level application routes.
 *
 * @remarks
 * All feature modules are lazy-loaded to keep the initial bundle small.
 * Every route except `/auth` is protected by {@link authGuard}.
 *
 * All tab-style pages (`/dashboard`, `/accept`, `/print`, `/sign`, `/upqueue`)
 * are root-level routes rendered by the root `IonRouterOutlet`. Each page
 * provides its own `AppTabBarComponent` footer. Use `navCtrl.navigateRoot`
 * for all navigation between these peers — `navigateForward` is only correct
 * when crossing into a nested route outlet.
 */
export const routes: Routes = [
  { path: '', redirectTo: 'auth', pathMatch: 'full' },

  // ── Authentication (public) ──────────────────────────────────────────────
  {
    path: 'auth',
    loadChildren: () => import('./features/auth/auth.routes').then((m) => m.AUTH_ROUTES),
    canActivate: [guestGuard],
  },

  // ── Root-level tab pages (protected) ─────────────────────────────────────
  {
    path: 'dashboard',
    loadComponent: () =>
      import('./features/dashboard/dashboard.component').then((m) => m.DashboardComponent),
    canActivate: [authGuard],
  },
  {
    path: 'accept',
    loadComponent: () =>
      import('./features/accept-queue/accept-queue.component').then((m) => m.AcceptQueueComponent),
    canActivate: [authGuard],
  },
  {
    path: 'print',
    loadComponent: () =>
      import('./features/print-queue/print-queue.component').then((m) => m.PrintQueueComponent),
    canActivate: [authGuard],
  },
  {
    path: 'sign',
    loadComponent: () =>
      import('./features/sign-queue/sign-queue.component').then((m) => m.SignQueueComponent),
    canActivate: [authGuard],
  },
  {
    path: 'upqueue',
    loadComponent: () =>
      import('./features/upqueue/upqueue.component').then((m) => m.UpqueueComponent),
    canActivate: [authGuard],
  },

  // ── Full-screen flows (root-level, protected) ────────────────────────────
  {
    path: 'jobs',
    loadChildren: () =>
      import('./features/job-list/job-list.routes').then((m) => m.JOB_LIST_ROUTES),
    canActivate: [authGuard],
  },
  {
    path: 'verify/:id',
    loadComponent: () =>
      import('./features/verify/verify.component').then((m) => m.VerifyComponent),
    canActivate: [authGuard],
  },
  {
    path: 'attempt/:id',
    loadComponent: () =>
      import('./features/attempt-log/attempt-log.component').then((m) => m.AttemptLogComponent),
    canActivate: [authGuard, attemptGuard],
  },
  {
    path: 'affidavit/:id',
    loadComponent: () =>
      import('./features/affidavit/affidavit.component').then((m) => m.AffidavitComponent),
    canActivate: [authGuard, signGuard],
  },

  { path: '**', redirectTo: 'auth' },
];
